import type { RootState } from '@src/core/store';
import { attributeTypeFromSlot, isAttributeSlot, ProductBookingSlot } from '../types/productBookingSlot';
import {
  attributeSlotSelector,
  isProductAdderStepValid,
  languageOptionsSelector,
  selectedProductAttributesSelector,
  type ProductStepType,
} from '@src/stores/product';
import { getSelectedLanguageValue } from './language/getSelectedLanguageValue';
import { getSelectedAttributeValue } from './attributes/selectedAttributes';

export type IsSlotCompleteOptions = {
  /**
   * When true, tariff completeness ignores `seatMapAllocationStatus.success`.
   * Sequential-navigation guards use this so the user can still re-open `time`
   * to fix an allocation failure that was triggered by the previously confirmed
   * hour. The add-to-cart guard never enables this — checkout stays blocked
   * until allocation succeeds.
   */
  skipSeatMapAllocationCheck?: boolean;
};

export const isSlotComplete = (
  slot: ProductBookingSlot,
  widgetId: string,
  steps: ProductStepType[],
  state: RootState,
  embedSlots: ProductBookingSlot[],
  options?: IsSlotCompleteOptions,
) => {
  if (isAttributeSlot(slot)) {
    if (!embedSlots.includes(slot)) {
      return true;
    }

    if (steps.includes('calendar') && !isProductAdderStepValid(widgetId, 'calendar')(state)) {
      return false;
    }

    const type = attributeTypeFromSlot(slot);
    const attributeSlotOptions = attributeSlotSelector(widgetId, type)(state)?.options ?? [];

    if (attributeSlotOptions.length === 0) {
      return true;
    }

    return Boolean(getSelectedAttributeValue(selectedProductAttributesSelector(widgetId)(state), type));
  }

  switch (slot) {
    case 'calendar': {
      if (!steps.includes('calendar')) {
        return true;
      }

      return isProductAdderStepValid(widgetId, 'calendar')(state);
    }
    case 'language': {
      if (!embedSlots.includes('language')) {
        return true;
      }

      if (steps.includes('calendar') && !isProductAdderStepValid(widgetId, 'calendar')(state)) {
        return false;
      }

      if (languageOptionsSelector(widgetId)(state).length === 0) {
        return true;
      }

      return Boolean(getSelectedLanguageValue(selectedProductAttributesSelector(widgetId)(state)));
    }
    case 'tariff':
      return isProductAdderStepValid(widgetId, 'tariff', {
        // Embed validates hour on the `time` slot when it is shown separately.
        skipTariffHourCheck: embedSlots.includes('time'),
        skipSeatMapAllocationCheck: options?.skipSeatMapAllocationCheck,
      })(state);
    case 'time':
      return isProductAdderStepValid(widgetId, 'time')(state);
    case 'seat_map':
      return isProductAdderStepValid(widgetId, 'seat_map')(state);
    case 'additionals':
      return isProductAdderStepValid(widgetId, 'additional')(state);
    default:
      return true;
  }
};
